/**
 * Tutor Page
 *
 * Voice-based literacy tutor powered by ElevenLabs Conversational AI.
 * Dark theme exception (like consonant practice + reading views).
 *
 * Features:
 * - Structured phonics lessons for children
 * - Module-scoped coaching for adults
 * - Client-side speaker identification via sherpa-onnx WASM
 * - Visual teaching display controlled by agent client tools
 * - 60-second inactivity timeout
 */

import React, { useState, useCallback, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useSelectedChild } from '@/contexts/ChildContext';
import { useTutorSession } from '@/lib/voice-agent/hooks/useTutorSession';
import { TutorDisplay } from '@/components/tutor/TutorDisplay';
import { TutorControls } from '@/components/tutor/TutorControls';
import { SpeakerPicker } from '@/components/tutor/SpeakerPicker';
import { InactivityGuard } from '@/components/tutor/InactivityGuard';

export const TutorPage: React.FC = () => {
  const { profile, loading: authLoading } = useAuth();
  const { selectedChild, isLoading: childLoading } = useSelectedChild();
  const [currentSpeaker, setCurrentSpeaker] = useState<string | undefined>();

  // Feature gate: /tutor only available on turneducation.com
  const isTutorDomain = window.location.hostname.includes('turneducation');
  if (!isTutorDomain) return <Navigate to="/" />;

  // Wait for auth + child data to load before deciding to redirect
  if (authLoading || childLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center">
        <div className="text-white/40 text-lg">Loading...</div>
      </div>
    );
  }

  if (!profile || !selectedChild) return <Navigate to="/" />;

  const {
    status,
    visualState,
    sessionData,
    errorMessage,
    lastSpeechTimestampRef,
    startSession,
    endSession,
    updateSpeaker,
  } = useTutorSession({
    childId: selectedChild.id,
    onSpeakerChange: (name, _role) => setCurrentSpeaker(name),
  });

  const handleManualSpeakerSelect = useCallback((name: string, role: 'child' | 'adult') => {
    setCurrentSpeaker(name);
    updateSpeaker(name, role);
  }, [updateSpeaker]);

  return (
    /* Dark theme exception: tutor page retains dark bg for optimal reading UX,
       same as consonant practice + reading views (per CLAUDE.md) */
    <div className="min-h-screen bg-[#0A0A0C] flex flex-col">
      {/* Visual display — takes most of the screen */}
      <div className="flex-1 flex items-center justify-center p-6">
        <TutorDisplay state={visualState} />
      </div>

      {/* Controls bar */}
      <div className="shrink-0 p-6 space-y-4 border-t border-white/5">
        {/* Speaker picker (manual fallback) */}
        {status === 'connected' && sessionData?.householdMembers && (
          <SpeakerPicker
            members={sessionData.householdMembers.map(m => ({
              name: m.name,
              role: m.role as 'child' | 'adult',
            }))}
            onSelect={handleManualSpeakerSelect}
            currentSpeaker={currentSpeaker}
          />
        )}

        {/* Session controls */}
        <TutorControls
          status={status}
          onStart={startSession}
          onStop={endSession}
        />

        {/* Error message */}
        {errorMessage && (
          <p className="text-[#FF453A] text-sm text-center">{errorMessage}</p>
        )}

        {/* Session info */}
        {status === 'connected' && sessionData && (
          <p className="text-white/30 text-xs text-center">
            {selectedChild.name} &middot; Module: {sessionData.dynamicVariables?.current_skill_description || ''}
          </p>
        )}
      </div>

      {/* Inactivity guard */}
      <InactivityGuard
        lastSpeechTimestamp={lastSpeechTimestampRef}
        onTimeout={endSession}
        enabled={status === 'connected'}
      />
    </div>
  );
};

export default TutorPage;
